import React from "react";
import { useEffect } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { RouteMap, RoutesContext } from "./routesctx";
import dynamic from "@dynamic";
import { ImportCollector } from "@dynamic/ImportCollector";
import createSSRApp from "../vite/ssr/entry";
import { RenderFn, SSRAppOptions } from "../vite/ssr/types";
import ReactDOMServer from "react-dom/server";
import { StaticRouter } from "react-router-dom";
import { ServerStyleSheets } from "@material-ui/core/styles";
import { HtmlTagDescriptor } from "./types";

export const routes: RouteMap = {
  "/": {
    component: dynamic(() => import("./pages/Home").then((x) => x.default)),
    name: "Home",
    path: "/",
    layout: dynamic(() => import("./pages/Home").then((x) => x.Layout)),
  },
  "/about": {
    component: dynamic(() => import("./pages/About").then((x) => x.default)),
    name: "About",
    path: "/about",
    layout: dynamic(() => import("./pages/About").then((x) => x.Layout)),
  },
};
const jssInitStyleTagId = "jss-server-side";
const appMountPoint = "app";

export function Main() {
  useEffect(() => {
    const jssStyles = document.querySelector(`#${jssInitStyleTagId}`);
    jssStyles?.parentElement?.removeChild(jssStyles);
  }, []);

  return (
    <RoutesContext.Provider value={routes}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </RoutesContext.Provider>
  );
}

interface RenderContext {
  url: string;
}

const ssrAppOptions: SSRAppOptions<RenderContext> = {
  async hydrate() {
    const importCollector = new ImportCollector();
    await importCollector.preload();
    ReactDOM.hydrate(
      importCollector.provide(<Main />),
      document.getElementById(appMountPoint)
    );
  },
  async render() {
    return [];
  },
};

if (import.meta.env.SSR) {
  const render: RenderFn<RenderContext> = async function (
    url: any,
    context: any
  ) {
    const importer = new ImportCollector();
    await importer.preload();

    const styleSheets = new ServerStyleSheets();
    const tags: HtmlTagDescriptor[] = [];

    const main = ReactDOMServer.renderToString(
      importer.provide(
        styleSheets.collect(
          <RoutesContext.Provider value={routes}>
            <StaticRouter location={url} context={context}>
              <App />
            </StaticRouter>
          </RoutesContext.Provider>
        )
      )
    );

    tags.push({
      name: "style",
      children: styleSheets.toString(),
      injectTo: "head",
      attrs: {
        id: jssInitStyleTagId,
      },
    });
    tags.push({
      children: main,
      injectTo: "body-prepend",
      name: "div",
      attrs: { id: appMountPoint },
    });
    tags.push({
      name: "script",
      injectTo: "head",
      children: importer.toClientScript(),
    });

    const preloadFiles = importer.preloadPaths();
    preloadFiles.forEach((file) => {
      const newTag = createPreloadTag(file);
      if (newTag) tags.push(newTag);
    });

    return tags as HtmlTagDescriptor[];
  };

  function createPreloadTag(file: string): HtmlTagDescriptor | undefined {
    if (file.endsWith(".js")) {
      return {
        name: "link",
        attrs: { rel: "modulepreload", crossorigin: true, href: file },
        injectTo: "head",
      };
    } else if (file.endsWith(".css")) {
      return {
        name: "link",
        attrs: { rel: "stylesheet", href: file },
        injectTo: "head",
      };
    }
  }

  ssrAppOptions.render = render;

  ssrAppOptions.generateRoutes = async function () {
    return [
      {
        url: "/",
        outPath: "/index.html",
      },
      {
        url: "/about",
        outPath: "/about.html",
      },
    ];
  };
}

export default createSSRApp(ssrAppOptions);
